import React, { useState } from "react";
import { CheckCircle2, Circle, Loader2, Plug, PlugZap } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton, useAlive } from "../phase2/shared";
import { useDemoBrand } from "@/lib/demoBrand";

const SYSTEMS = ["Legacy AS/400 ledger", "County permit portal", "QuickBooks Desktop", "Custom CRM (in-house)"];
const STAGES = ["Authenticated", "Fields mapped · 18 of 18", "Records syncing"];
const TOTAL = 1284;

// Pick a niche system, connect it, and watch the records stream in.
export default function CustomIntegrationCard() {
  const { name } = useDemoBrand();
  const [sys, setSys] = useState(0);
  const [stage, setStage] = useState(-1);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const alive = useAlive();

  const connect = async () => {
    setBusy(true); setStage(-1); setCount(0);
    for (let i = 0; i < STAGES.length; i += 1) { await sleep(650); if (!alive.current) return; setStage(i); }
    for (let n = 1; n <= 20; n += 1) { await sleep(70); if (!alive.current) return; setCount(Math.round((TOTAL * n) / 20)); }
    setStage(STAGES.length);
    setBusy(false);
  };

  return (
    <ActiveCardShell id="T7300cr" kicker="CUSTOM BUILD" title="Custom Integration Connector" Icon={Plug}
      actions={<RunButton tid="veroogle-t7300-run" running={busy} onClick={connect} Icon={busy ? Loader2 : PlugZap} label="Connect" />}>
      <div className="flex flex-wrap gap-2 mb-4">
        {SYSTEMS.map((s, i) => (
          <button key={s} type="button" disabled={busy} onClick={() => { setSys(i); setStage(-1); setCount(0); }} data-testid={`veroogle-t7300-system-${i}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${sys === i ? "border-brand-cyan/60 bg-brand-cyan/10 text-white" : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border border-white/10 bg-navy-950/50 p-4">
        <div className="text-center text-sm text-white truncate" data-testid="veroogle-t7300-source">{SYSTEMS[sys]}</div>
        <div className="relative w-16 sm:w-28 h-[2px] bg-white/10 overflow-hidden rounded">
          {stage >= 2 && <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-brand-cyan to-transparent" style={{ animation: "nexSlide 0.9s linear infinite" }} />}
        </div>
        <div className="text-center text-sm font-semibold text-white truncate">{name}</div>
      </div>
      <style>{"@keyframes nexSlide{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}"}</style>
      <ul className="mt-3 space-y-1.5">
        {STAGES.map((s, i) => (
          <li key={s} data-testid={`veroogle-t7300-stage-${i}`} className={`flex items-center gap-2 text-sm ${stage >= i ? "text-white" : "text-slate-500"}`}>
            {stage > i || stage === STAGES.length ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : stage === i ? <Loader2 className="w-4 h-4 text-brand-cyan animate-spin" /> : <Circle className="w-4 h-4 text-slate-600" />}
            {s}{i === 2 && stage >= 2 && <span className="ml-auto font-mono text-xs text-brand-cyan tabular-nums" data-testid="veroogle-t7300-count">{count.toLocaleString()} / {TOTAL.toLocaleString()}</span>}
          </li>
        ))}
      </ul>
      {stage === STAGES.length && (
        <div data-testid="veroogle-t7300-outcome" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in">
          <CheckCircle2 className="w-4 h-4" /> Synced · {TOTAL.toLocaleString()} records · live
        </div>
      )}
    </ActiveCardShell>
  );
}
